
import { localStorageHyperDashboardTools } from './localStorageHyperDashboardTools';


/**
 * Provides utility methods to work with URL.
 * @type {{getParameters: (function(): {}), getParameter: (function(*=, *=): *)}}
 */
export const urlTools = {
    /**
     * Returns Map of all parameter names as key and it's values as value.
     *
     * Given: url?param1=value1&param2=2 will return Map like this:
     * Map<string, string> =>
     * map["param1"] = "value1" (String)
     * map["param2"] = 2 (Number)
     *
     * @return Map<string, string | number> of all parameter name to value
     */
    getParameters: (): Map<string, string | number> => {
        const params = new Map<string, string | number>();
        window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/g, (substring: string, key: string, value: string): any => {
            const valueToSet = Number.isFinite(+value) ? Number(value) : value;
            params.set(key, valueToSet);
        });

        return params;
    },

    /**
     * Returns URL parameter or given default value.
     * @param name name of the parameter
     * @param defaultValue value to return if parameter was not found (defaults to null)
     * @return parameter value or given default value (which is null if omitted)
     */
    getParameter<T>(name: string, defaultValue: T | null = null): any {
        let returnValue: any = defaultValue;

        if(window.location.href.indexOf(name) > -1) {
            const paramValue = urlTools.getParameters();
            if(paramValue.get(name) && paramValue.get(name) !== null) {
                returnValue = paramValue.get(name);
            }
        }

        if(returnValue === defaultValue) {
            if (
                localStorageHyperDashboardTools.getValueByPropName(
                    'isCaseNumberConsoleShown',
                    false as any,
                )
            ) {
                console.warn(
                    `Parameter ${name} not found, returning default value: ${returnValue}`,
                );
            }
        }

        return returnValue;
    },

    getCustomEndpoints: (...args) => {
        
        const [ {
            endpointsConfig,
            endpointsValues,
            hostNames = {},
            prefixes = {},
            isCustom,
            customConfigName = undefined
        } ] = args;
        
        return endpointsConfig.reduce((summary, host) => {

            const {
                hostName,
                customHostName,
                prefix,
                customPrefix,
                endpoints
            } = host;

            const resolveEndpoint = endpointName => {

                switch(true) {
                    case !!endpointsValues[endpointName]:
                        return endpointsValues[endpointName];
                    case !!(endpointsValues[customConfigName] && endpointsValues[customConfigName][endpointName]):
                        return endpointsValues[customConfigName][endpointName];
                    default:
                        console.log(`Value for ${endpointName} endpoint has not been found`);
                        return null;
                }
            };

            const resolveHost = () => {
                return (customHostName && isCustom) ? hostNames[customHostName] : hostName;
            };

            const resolvePrefix = () => {
                return (customPrefix && isCustom) ? prefixes[customPrefix] : prefix;
            };

            return {
                ...summary,
                ...endpoints.reduce((hostSummary, endpointName) => {
                    return {
                        ...hostSummary,
                        [endpointName]: `${resolveHost()}${resolvePrefix()}${resolveEndpoint(endpointName)}`
                    };
                }, {})
            };
        } ,{});
    },

    // Add JS Doc and TypeScript
    
    getEndpoints: (...args) => {
        
        const [ {
            endpointsConfig,
            customConfigName
        } ] = args;
        
        const {
            hostName,
            endpoints = {}
        } = endpointsConfig;

        const resolveHost = () => {            
            if (hostName && hostName.trim()) {
                return hostName;
            }
            
            return customConfigName ? 'http://localhost:9000' : window.location.origin;
        };

        const resolvedEndpoints = Object.entries(endpoints).reduce((hostSummary, [endpointName, endpointValue]) => {
            return {
                ...hostSummary,
                [endpointName]: `${resolveHost()}${endpointValue}`
            };
        }, {});
    
        return {
            ...resolvedEndpoints
        };
    }
};
